package com.siriusxm.pia.views.unifiedaggregator.events

import com.siriusxm.pia.components.OpenEndedRange
import com.siriusxm.pia.components.Period
import com.siriusxm.pia.components.PeriodType
import com.siriusxm.pia.components.TimeRange
import com.siriusxm.pia.views.unifiedaggregator.Query
import unifiedcontent.aggregator.EventQueryOption
import kotlin.time.Instant

/** The query parameter carrying the selected time range, relative or absolute. */
private const val RANGE = "range"

/** The query parameter carrying the Add/Update/Remove filter. */
private const val EVENT_TYPE = "eventType"

/** The query parameter carrying the entity type filter. */
private const val ENTITY_TYPE = "entityType"

/** The query parameter carrying the free-text error message filter. */
private const val SEARCH = "search"

/** The query parameter carrying the Outgoing/Incoming query direction. */
private const val QUERY_TYPE = "queryType"

/** Separates the two ends of an absolute range in the [RANGE] parameter. */
private const val ABSOLUTE_SEPARATOR = ".."

/**
 * [PeriodType] by its short code, for decoding the relative form of [RANGE].
 *
 * Hand-written because `PeriodType` is a sealed *class* of objects with no `entries`, so there is no
 * compiler-checked enumeration to derive this from. If a period type is ever added upstream and not
 * added here, a URL naming it decodes to no range rather than to the wrong one — see
 * [parseTimeRange].
 */
private val periodTypesByShortCode: Map<String, PeriodType> = listOf(
    PeriodType.Minutes,
    PeriodType.Hours,
    PeriodType.Days,
    PeriodType.Weeks,
    PeriodType.Months
).associateBy { it.short }

/**
 * The filter selections for a producer's Events or Errors view, as a single value that can be read
 * from and written to a URL query string.
 *
 * ### Why this is not part of the navigation key
 *
 * `ProducerDetail` deliberately does **not** carry these. Filters are query state, and the typed
 * destination keys own path segments only; the two meet in one place,
 * `AggregatorService.navigate(key, query)`. Making them constructor fields on the key would mean
 * every existing `NavLink(ProducerDetail(id))` silently also meant "and clear the filters", would
 * make the navigation layer depend on the events UI, and would not generalise to the next filtered
 * page. The full argument is in `plans/2026-09-01-UCA-1028-producer-filter-url-state.md` §3.
 *
 * ### Absent means unfiltered
 *
 * Every field is nullable and null means "no filter", so [EventFilters] with nothing set serialises
 * to an empty [Query] — and an empty query emits no `?` at all. That is what makes links created
 * before filters were in the URL identical to, not merely equivalent to, the links created now.
 *
 * The Events tab uses [range], [eventType] and [entityType]; the Errors tab uses [range] and
 * [search]; the entity-type and entity Events views (UCA-1039) additionally use [queryType]. They
 * are modelled together rather than as separate types because they share the range and because
 * all of these views are variations on the same filtered event list — a user switching tabs keeps
 * whichever parameters still apply, and the ones that do not simply are not written.
 *
 * @property range the selected time range. The only field that drives a server query; the rest
 *   filter the loaded events client-side.
 * @property eventType the Add/Update/Remove filter, or null for all types.
 * @property entityType the entity type id to restrict to, or null for all types.
 * @property search free text matched against the error reason, or null for no search. Normalised so
 *   blank is always null — a blank search is the same view as no search, and letting both exist
 *   would put `?search=` in the URL for a box the user emptied again.
 * @property queryType the Outgoing/Incoming query direction, or null when the view requires the
 *   user to choose one before it queries at all. Unlike the other fields this one also drives which
 *   server query runs (not just client-side filtering), same as [range].
 */
data class EventFilters(
    val range: TimeRange? = null,
    val eventType: EventTypeFilterOption? = null,
    val entityType: String? = null,
    val search: String? = null,
    val queryType: EventQueryOption? = null
) {
    /**
     * These filters as query parameters, in a fixed order, omitting everything unset.
     *
     * Only the parameters this type knows about are emitted; any other parameter in the URL the
     * user arrived on is dropped. Preserving unknown parameters would make a typo'd one sticky and
     * impossible to clear through the UI, and nothing else on this page uses the query.
     */
    fun toQuery(): Query = Query.Empty
        .with(RANGE, range?.let { encodeTimeRange(it) })
        .with(EVENT_TYPE, eventType?.name)
        .with(ENTITY_TYPE, entityType?.takeIf { it.isNotBlank() })
        .with(SEARCH, search?.takeIf { it.isNotBlank() })
        .with(QUERY_TYPE, queryType?.name)

    companion object {
        /**
         * Decode filters from a URL query.
         *
         * Total, and forgiving field by field: a value that cannot be understood leaves that filter
         * unset rather than failing. That is deliberate — the fallback for a malformed filter is the
         * unfiltered view, which is a page the user can act on, and there is no route by which this
         * layer could render an error anyway.
         *
         * Note that [entityType] is not validated against the producer's allowed types here, because
         * this function does not know them. The Events view drops an id it cannot offer in its
         * dropdown, so `?entityType=does-not-exist` shows "All entity types" rather than an empty
         * table.
         */
        fun from(query: Query): EventFilters = EventFilters(
            range = query[RANGE]?.let { parseTimeRange(it) },
            eventType = query[EVENT_TYPE]?.let { parseEventType(it) },
            entityType = query[ENTITY_TYPE]?.takeIf { it.isNotBlank() },
            search = query[SEARCH]?.takeIf { it.isNotBlank() },
            queryType = query[QUERY_TYPE]?.let { parseQueryType(it) }
        )
    }
}

/**
 * Serialise a time range for the [RANGE] parameter, or null if it carries nothing serialisable.
 *
 * A relative range wins over an absolute one when a [TimeRange] somehow holds both, matching
 * [TimeRange.humanString]'s precedence so the URL agrees with what the picker displays.
 *
 * `dayAligned` is not encoded. Both views that use this codec render their picker at
 * `TimeRangeGranularity.Time`, where the flag is structurally always false. A day-granularity view
 * adopting this codec must add a parameter for it; the round-trip check in the plan is what would
 * catch its absence.
 */
internal fun encodeTimeRange(range: TimeRange): String? {
    range.relative?.let { return it.humanString() }

    val absolute = range.absolute ?: return null
    if (absolute.start == null && absolute.end == null) return null
    return "${absolute.start ?: ""}$ABSOLUTE_SEPARATOR${absolute.end ?: ""}"
}

/**
 * Decode the [RANGE] parameter, or null if it is not a range we recognise.
 *
 * Two forms:
 *
 * - **relative** — a count followed by a [PeriodType] short code, e.g. `12h`, `1d`, `3mo`. The digits
 *   are split from the suffix and the suffix is matched *exactly*, never by prefix: `Minutes.short`
 *   is `"m"` and `Months.short` is `"mo"`, so a prefix match would decode `3mo` as three minutes.
 * - **absolute** — two ISO-8601 instants separated by `..`, either side optionally empty for an
 *   open end.
 *
 * `Instant.parse` throws on malformed input and [TimeRange]'s constructor throws when given neither
 * end, so the whole thing runs inside a `runCatching`: a range we cannot make sense of is no range.
 */
internal fun parseTimeRange(raw: String): TimeRange? = runCatching {
    val value = raw.trim()
    if (value.isEmpty()) return null

    if (value.contains(ABSOLUTE_SEPARATOR)) {
        val start = value.substringBefore(ABSOLUTE_SEPARATOR).takeIf { it.isNotBlank() }
        val end = value.substringAfter(ABSOLUTE_SEPARATOR).takeIf { it.isNotBlank() }
        if (start == null && end == null) return null
        return@runCatching TimeRange(
            absolute = OpenEndedRange(start?.let { Instant.parse(it) }, end?.let { Instant.parse(it) })
        )
    }

    val count = value.takeWhile { it.isDigit() }.toIntOrNull() ?: return null
    if (count <= 0) return null
    val type = periodTypesByShortCode[value.dropWhile { it.isDigit() }] ?: return null
    TimeRange(relative = Period(type, count))
}.getOrNull()

/**
 * Decode the [QUERY_TYPE] parameter, or null if it names no direction we have.
 *
 * Matched against [EventQueryOption.entries] rather than [EventQueryOption.fromName] — `fromName`
 * is total and returns a `_Unknown` instance for anything it doesn't recognise, which this layer's
 * "malformed value leaves the field unset" convention treats the same as absent.
 */
private fun parseQueryType(raw: String): EventQueryOption? =
    EventQueryOption.entries.find { it.name.equals(raw.trim(), ignoreCase = true) }

/**
 * Decode the [EVENT_TYPE] parameter, or null if it names no option we have.
 *
 * Case-insensitive so a hand-typed `?eventType=add` works; `valueOf` would throw on anything else,
 * so the lookup is by comparison instead.
 */
private fun parseEventType(raw: String): EventTypeFilterOption? =
    EventTypeFilterOption.entries.find { it.name.equals(raw.trim(), ignoreCase = true) }
